if (!customElements.get('gift-card-builder')) {
  customElements.define(
    'gift-card-builder',
    class GiftCardBuilder extends HTMLElement {
      connectedCallback() {
        this.previewImage = this.querySelector('[data-gift-card-preview]');
        this.previewAmount = this.querySelector('[data-gift-card-preview-amount]');
        this.price = this.querySelector('[data-gift-card-price]');
        this.variantInput = this.querySelector('[data-gift-card-variant]');
        this.recipientFields = this.querySelector('[data-gift-card-recipient-fields]');
        this.recipientInputs = [...this.querySelectorAll('[data-gift-card-recipient-input]')];
        this.quantityInput = this.querySelector('[data-gift-card-quantity]');
        this.submitButton = this.querySelector('[data-gift-card-submit]');
        this.form = this.querySelector('form');

        if (typeof subscribe === 'function') {
          const amountIds = [...this.querySelectorAll('[data-gift-card-amount]')].map((input) => input.value);

          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
            if (event.source !== 'product-form') return;
            if (event.productVariantId && !amountIds.includes(String(event.productVariantId))) return;

            this.resetRecipientFields();
          });
        }

        this.addEventListener('click', this.handleClick);
        this.addEventListener('change', this.handleChange);
        this.addEventListener('input', this.handleInput);
        this.form?.addEventListener('submit', this.handleSubmitValidation, true);
        this.setRecipientMode(this.querySelector('[name="gift-card-delivery-mode"]:checked')?.value || 'self');
        this.setDeliveryDateLimits();

        // The radios can already be in a non-default state before this element upgrades
        // (a click during the defer window, or browser form-state restoration on reload),
        // and hidden inputs are not restored. Re-sync so the variant id matches the UI.
        const checkedAmount = this.querySelector('[data-gift-card-amount]:checked');
        if (checkedAmount) this.selectAmount(checkedAmount);
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.handleClick);
        this.removeEventListener('change', this.handleChange);
        this.removeEventListener('input', this.handleInput);
        this.form?.removeEventListener('submit', this.handleSubmitValidation, true);
        this.cartUpdateUnsubscriber?.();
      }

      handleClick = (event) => {
        const previewButton = event.target.closest('[data-gift-card-image]');
        if (previewButton) {
          this.selectPreview(previewButton);
          return;
        }

        const quantityButton = event.target.closest('[data-gift-card-quantity-change]');
        if (quantityButton) {
          this.changeQuantity(Number(quantityButton.dataset.giftCardQuantityChange));
        }
      };

      handleChange = (event) => {
        const amountInput = event.target.closest('[data-gift-card-amount]');
        if (amountInput) {
          this.selectAmount(amountInput);
          return;
        }

        const deliveryMode = event.target.closest('[name="gift-card-delivery-mode"]');
        if (deliveryMode) this.setRecipientMode(deliveryMode.value);
      };

      handleInput = (event) => {
        const field = event.target.closest('[data-gift-card-recipient-input]');
        if (field && field.type !== 'hidden') this.validateField(field);
      };

      handleSubmitValidation = (event) => {
        if (this.validateRecipientFields()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      };

      selectPreview(button) {
        if (!this.previewImage) return;

        // srcset takes precedence over src when selecting an image source, so both
        // must be rewritten or the preview keeps showing the originally rendered media.
        this.previewImage.srcset = button.dataset.giftCardImageSrcset || '';
        this.previewImage.src = button.dataset.giftCardImage;
        this.previewImage.alt = button.dataset.giftCardImageAlt || '';
        this.querySelectorAll('[data-gift-card-image]').forEach((item) => {
          item.setAttribute('aria-pressed', String(item === button));
        });
      }

      selectAmount(input) {
        if (this.variantInput) this.variantInput.value = input.value;
        if (this.price) this.price.textContent = input.dataset.giftCardAmountLabel;
        if (this.previewAmount) this.previewAmount.textContent = input.dataset.giftCardAmountLabel;
        this.updateAvailability(input);
      }

      // The submit button's state is rendered from the initially selected variant, so a deep
      // link to a sold-out amount would otherwise stay unpurchasable even after the shopper
      // picks an available one.
      updateAvailability(input) {
        if (!this.submitButton) return;

        const available = input.dataset.giftCardAvailable !== 'false';
        const label = this.submitButton.querySelector('span');

        this.submitButton.disabled = !available;

        if (available) {
          this.submitButton.removeAttribute('aria-disabled');
        }
        else {
          this.submitButton.setAttribute('aria-disabled', 'true');
        }

        if (label) {
          const next = available
            ? this.submitButton.dataset.giftCardAddLabel
            : this.submitButton.dataset.giftCardSoldOutLabel;

          if (next) label.textContent = next;
        }
      }

      // Without this the recipient's email, name and message persist after a successful add,
      // so a second gift card silently reuses the first recipient's details.
      resetRecipientFields() {
        const selfOption = this.querySelector('[name="gift-card-delivery-mode"][value="self"]');
        if (selfOption) selfOption.checked = true;

        this.recipientInputs.forEach((input) => {
          if (input.type !== 'hidden') input.value = '';
        });

        this.setRecipientMode('self');
      }

      setRecipientMode(mode) {
        const sendAsGift = mode === 'gift';
        this.recipientFields.hidden = !sendAsGift;

        this.recipientInputs.forEach((input) => {
          input.disabled = !sendAsGift;
        });

        const recipientEmail = this.querySelector('[data-gift-card-recipient-email]');
        if (recipientEmail) recipientEmail.required = sendAsGift;

        if (!sendAsGift) this.clearRecipientErrors();
      }

      setDeliveryDateLimits() {
        const sendOn = this.querySelector('[data-gift-card-send-on]');
        if (!sendOn) return;

        const today = new Date();
        const maxDate = new Date(today);
        maxDate.setDate(today.getDate() + 90);
        sendOn.min = this.formatDate(today);
        sendOn.max = this.formatDate(maxDate);

        const timezoneOffset = this.querySelector('[data-gift-card-timezone-offset]');
        if (timezoneOffset) timezoneOffset.value = String(new Date().getTimezoneOffset());
      }

      changeQuantity(change) {
        const minimum = Number(this.quantityInput.min) || 1;
        const currentValue = Number(this.quantityInput.value) || minimum;
        this.quantityInput.value = String(Math.max(minimum, currentValue + change));
      }

      validateRecipientFields() {
        const sendAsGift = this.querySelector('[name="gift-card-delivery-mode"]:checked')?.value === 'gift';
        if (!sendAsGift) return true;

        const fields = this.recipientInputs.filter((input) => input.type !== 'hidden');
        const firstInvalidField = fields.find((field) => !this.validateField(field));

        if (firstInvalidField) {
          firstInvalidField.focus();
          return false;
        }

        return true;
      }

      validateField(field) {
        let message = '';
        const label = field.dataset.giftCardValidationLabel || 'This field';

        if (field.validity.valueMissing) {
          message = `${label} is required.`;
        } else if (field.validity.typeMismatch) {
          message = 'Enter a valid email address.';
        } else if (field.validity.rangeUnderflow) {
          message = 'Choose today or a later date.';
        } else if (field.validity.rangeOverflow) {
          message = 'Choose a delivery date within the next 90 days.';
        } else if (field.validity.tooLong) {
          message = `${label} is too long.`;
        }

        this.setFieldError(field, message);
        return !message;
      }

      setFieldError(field, message) {
        const error = field.closest('.gift-card-builder__field')?.querySelector('[data-gift-card-field-error]');
        if (!error) return;

        error.textContent = message;
        error.hidden = !message;

        if (message) {
          field.setAttribute('aria-invalid', 'true');
          field.setAttribute('aria-describedby', error.id);
        } else {
          field.removeAttribute('aria-invalid');
          field.removeAttribute('aria-describedby');
        }
      }

      clearRecipientErrors() {
        this.recipientInputs.forEach((field) => {
          if (field.type !== 'hidden') this.setFieldError(field, '');
        });
      }

      formatDate(date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      }
    }
  );
}
